import { useEffect } from 'react';
import { ChevronLeft, Search, X } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useRecordSearch } from '@/store/searchHistoryStore';
import {
  useSearchOverlayOpen,
  useOpenSearchOverlay,
  useCloseSearchOverlay,
  useSearchQuery,
  useSetSearchQuery,
} from '@/store';

const TEST_ID_PREFIX = 'header-search';

export interface HeaderSearchProps {
  /**
   * Compact mode mirrors the pinned header: shrinks the trigger icon so it
   * lines up with the compact category tabs. Forwarded from {@link CategoryTabs}.
   */
  compact?: boolean;
}

/**
 * In-header live search trigger + field (ARC-113). Sits where the category-tab
 * search icon used to be: tapping the icon opens search *in place* — the tabs
 * animate out and this field grows from a small pill to fill the row. Open/close
 * state and the query live in the search-overlay store; the results body that
 * pairs with this field is {@link SearchOverlayBody}, rendered in the page
 * content area (so it sits on the page background, not a separate overlay).
 * Replaces the former `/search` route.
 */
export function HeaderSearch({ compact = false }: HeaderSearchProps) {
  const isOpen = useSearchOverlayOpen();
  const open = useOpenSearchOverlay();
  const close = useCloseSearchOverlay();
  const query = useSearchQuery();
  const setQuery = useSetSearchQuery();
  const recordSearch = useRecordSearch();

  // Escape-to-close while open.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close]);

  // WKWebView compositing fix: sticky layers cache their bitmap and skip
  // repaints when children change via JS/CSS. A micro-scroll forces the
  // compositor to invalidate and repaint the sticky header.
  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      window.scrollBy(0, 1);
      requestAnimationFrame(() => window.scrollBy(0, -1));
    });
  }, [isOpen]);

  // The trigger stays in the flex flow whether open or closed, so opening never
  // changes the row's layout (the tabs must not jump sideways). While open it's
  // inert and its icon shrinks away once the field has reached full width,
  // revealing the back/close arrow beneath; the field overlays it absolutely.
  return (
    <>
      <Button
        data-testid={`${TEST_ID_PREFIX}-trigger`}
        variant="ghost"
        size="icon"
        className={cn('ml-2 shrink-0', isOpen && 'pointer-events-none relative z-20')}
        aria-label="Search"
        aria-hidden={isOpen || undefined}
        tabIndex={isOpen ? -1 : undefined}
        onClick={open}
      >
        {!isOpen && <Search className={cn('text-primary', compact ? 'size-[18px]' : 'size-[22px]')} />}
      </Button>

      {isOpen && (
        <div
          data-search-surface
          className="search-field-expand absolute inset-y-0 left-0 z-10 flex w-full items-center gap-2 pl-2 pr-2"
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close search"
            data-testid={`${TEST_ID_PREFIX}-close`}
            className="shrink-0 text-muted hover:text-primary"
          >
            <ChevronLeft className="size-5" />
          </button>

          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') recordSearch(query);
              }}
              placeholder="Search here"
              aria-label="Search events"
              data-testid={`${TEST_ID_PREFIX}-input`}
              className="h-10 rounded-full border-[1.5px] border-purple bg-surface pl-10 pr-10 focus:border-purple focus:ring-0"
            />
            {query.length > 0 && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                data-testid={`${TEST_ID_PREFIX}-clear`}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted hover:text-primary"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
